import { useState } from "react";
import { Download, FileText, FileSignature, AlertOctagon, FileEdit, Loader2, Lock, ArrowRight } from "lucide-react";
import { getDocumentDownloadUrl } from "@/lib/api";
import { toast } from "sonner";

const DOCS = [
  {
    type: "opzegbrief",
    title: "Opzegbrief",
    description: "Professionele opzegging van je huidige energiecontract",
    icon: FileSignature,
    color: "blue",
  },
  {
    type: "klachtbrief",
    title: "Klachtbrief",
    description: "Klacht aan je energieleverancier over voorwaarden of service",
    icon: AlertOctagon,
    color: "amber",
  },
  {
    type: "bezwaarbrief",
    title: "Bezwaarbrief factuur",
    description: "Bezwaar tegen een onjuiste of onduidelijke factuur",
    icon: FileText,
    color: "red",
  },
  {
    type: "meterstand",
    title: "Verzoek correctie meterstand",
    description: "Formele aanvraag tot correctie van foutieve meterstanden",
    icon: FileEdit,
    color: "emerald",
  },
];

const COLOR_CLASSES = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-100",    btn: "bg-blue-700 hover:bg-blue-800" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-100",   btn: "bg-amber-600 hover:bg-amber-700" },
  red:     { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-100",     btn: "bg-red-600 hover:bg-red-700" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-100", btn: "bg-emerald-700 hover:bg-emerald-800" },
};

export default function DocumentsSection({ analysisId, unlocked = false, onUnlockClick }) {
  const [loadingType, setLoadingType] = useState(null);

  const handleDownload = async (docType) => {
    if (!unlocked) {
      onUnlockClick?.();
      return;
    }
    setLoadingType(docType);
    try {
      const url = getDocumentDownloadUrl(analysisId, docType);
      const response = await fetch(url);
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || "Download mislukt");
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${docType}_${analysisId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Document gedownload!");
    } catch (err) {
      toast.error(`Download mislukt: ${err.message || err}`);
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <section className="mt-12" data-testid="documents-section">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
            Brievenbundel
          </span>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {unlocked ? "Je 4 brieven, kant-en-klaar" : "Brieven beschikbaar in de bundel"}
          </h2>
          <p className="mt-2 text-slate-600 max-w-2xl">
            {unlocked
              ? "Speciaal opgesteld op basis van jouw analyse. Vul de placeholders in en verstuur."
              : "Upgrade naar de brievenbundel om alle 4 PDF brieven direct te kunnen downloaden."}
          </p>
        </div>
        {!unlocked && (
          <button
            onClick={onUnlockClick}
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-all"
            data-testid="docs-unlock-cta"
          >
            <Lock className="h-4 w-4" /> Ontgrendel bundel
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {DOCS.map((doc) => {
          const c = COLOR_CLASSES[doc.color];
          const isLoading = loadingType === doc.type;
          return (
            <div
              key={doc.type}
              className={`relative bg-white border rounded-2xl p-6 transition-all hover:shadow-[0_8px_30px_-12px_rgba(15,23,42,0.10)] ${
                unlocked ? "border-slate-200 hover:border-blue-200" : "border-slate-200"
              }`}
              data-testid={`doc-card-${doc.type}`}
            >
              <div className="flex items-start gap-4">
                <div className={`h-12 w-12 rounded-xl ${c.bg} border ${c.border} grid place-items-center flex-shrink-0`}>
                  <doc.icon className={`h-6 w-6 ${c.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                    {doc.title}
                    {!unlocked && <Lock className="h-3.5 w-3.5 text-slate-400" />}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">{doc.description}</p>
                  <button
                    onClick={() => handleDownload(doc.type)}
                    disabled={isLoading}
                    className={`mt-4 inline-flex items-center gap-2 font-medium text-sm rounded-lg px-4 py-2 transition-all disabled:opacity-60 ${
                      unlocked
                        ? `${c.btn} text-white`
                        : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                    }`}
                    data-testid={`doc-download-${doc.type}`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Brief genereren...
                      </>
                    ) : unlocked ? (
                      <>
                        <Download className="h-4 w-4" />
                        Download PDF
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4" />
                        Vergrendeld
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
